import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from 'hooks/useAuth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CriarListaPayload,
  criarLista as criarListaService,
  listarMinhasListas,
  trocarTemplateLista as trocarTemplateListaService,
} from 'services/adminListas';
import { trackCreateListSuccess } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import {
  setGoogleAdsUserData,
  trackGoogleAdsConversion,
  trackGoogleTagEvent,
} from 'utils/googleTag';
import { trackEvent } from 'utils/tracking';
import { logM } from 'utils/observability';

const DEVICE_ID_STORAGE_KEY = 'quick_create_device_id_v1';

const TEMPLATE_OPTIONS = [
  { slug: 'cha-de-bebe', label: 'Chá de bebê' },
  { slug: 'aniversario', label: 'Aniversário' },
  { slug: 'casamento', label: 'Casamento' },
  { slug: 'vaquinha', label: 'Vaquinha' },
] as const;

const SINGLE_OPTION_LABEL_BY_TEMPLATE: Record<string, string> = {
  'cha-de-bebe': 'Chá de bebê',
  casamento: 'Casamento',
};

const gerarSlugAleatorio = () =>
  Math.random()
    .toString(36)
    .replace(/[^a-z0-9]+/gi, '')
    .slice(-5) || 'lista';

const CriarListaPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, loginAsGuest, user } = useAuth();
  const brandAssets = BUILD_PROFILE.assets;
  const brandTheme = BUILD_PROFILE.theme;
  const submitLockRef = useRef(false);
  const autoCreateStartedRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // true enquanto checa se o usuário já tem lista (não mostra botões nem auto-cria à toa)
  const [verificandoLista, setVerificandoLista] = useState(true);

  const templateFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('template') || '';
  }, [location.search]);

  const allowedTemplates = useMemo(() => {
    if (BUILD_PROFILE.forceTemplateSlug) {
      return [
        {
          slug: BUILD_PROFILE.forceTemplateSlug,
          label: SINGLE_OPTION_LABEL_BY_TEMPLATE[BUILD_PROFILE.forceTemplateSlug] || 'Modelo',
        },
      ];
    }

    return TEMPLATE_OPTIONS;
  }, []);

  const selectedTemplateSlug = useMemo(() => {
    if (BUILD_PROFILE.forceTemplateSlug) {
      return BUILD_PROFILE.forceTemplateSlug;
    }
    const optionExists = allowedTemplates.some((item) => item.slug === templateFromQuery);
    return optionExists ? templateFromQuery : '';
  }, [allowedTemplates, templateFromQuery]);

  const obterDeviceId = () => {
    const existente = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existente) return existente;

    const novo = (
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    ).replace(/[^a-zA-Z0-9_-]/g, '');

    localStorage.setItem(DEVICE_ID_STORAGE_KEY, novo);
    return novo;
  };

  const executarCriacao = useCallback(
    async (templateSlug: string) => {
      if (submitLockRef.current) {
        trackEvent('create_list_blocked_inflight', {
          source: 'template_picker',
        });
        return;
      }

      submitLockRef.current = true;

      if (loading) {
        trackEvent('create_list_blocked_session_loading', {
          source: 'template_picker',
        });
        setInfo('Validando sua sessão, tente novamente em alguns segundos.');
        submitLockRef.current = false;
        return;
      }

      setCreating(true);
      setError('');
      setInfo('');

      const templateSlugEfetivo =
        templateSlug || BUILD_PROFILE.forceTemplateSlug || 'personalizado';

      const payload: CriarListaPayload = {
        tituloLista: 'Minha lista',
        publica: false,
        slug: gerarSlugAleatorio(),
        templateSlug: templateSlugEfetivo || undefined,
        tipoLista: templateSlugEfetivo,
      };

      try {
        if (!isAuthenticated) {
          const deviceId = obterDeviceId();
          await loginAsGuest(deviceId);
        }

        const data = await criarListaService(payload);
        const listaCriada = data?.lista;

        if (!listaCriada?.id) {
          throw new Error('Não foi possível identificar a lista criada.');
        }

        trackEvent('create_list_success', {
          source: 'template_picker',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo,
        });
        logM('Criar', templateSlugEfetivo);
        trackCreateListSuccess({
          source: 'template_picker',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo,
          guestFlow: !isAuthenticated,
          brand: BUILD_PROFILE.brand,
        });
        trackGoogleTagEvent('create_list', {
          event_category: 'engagement',
          event_label: BUILD_PROFILE.brand,
          list_id: listaCriada.id,
          template_slug: templateSlugEfetivo,
          guest_flow: !isAuthenticated,
        });
        setGoogleAdsUserData({
          email: user?.email,
          phoneNumber: user?.telefone,
          fullName: user?.nome,
        });
        trackGoogleAdsConversion(
          BUILD_PROFILE.googleAdsId,
          BUILD_PROFILE.googleAdsConversionLabel,
          listaCriada.id
        );
        const slugCriado = String(listaCriada.slug || '').trim();
        if (slugCriado) {
          navigate(`/${slugCriado}`, { replace: true });
        } else {
          navigate('/admin/listas', {
            replace: true,
            state: { mensagemSucesso: 'Lista criada com sucesso.' },
          });
        }
      } catch (err: any) {
        const rawMessage = String(err?.response?.data?.error || '').trim();

        // Usuário já tem uma lista — redirecionar direto para Minhas listas
        const normalizedForCheck = rawMessage
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        if (normalizedForCheck.includes('voce ja tem uma lista')) {
          // Já tem lista: aplica o tipo escolhido na lista existente em vez de ignorar
          try {
            const listas = await listarMinhasListas();
            const listaExistente = listas.find((item) => item.slug);
            if (listaExistente) {
              const templateAtual = listaExistente.template?.slug ?? listaExistente.tipoLista;
              if (templateAtual !== templateSlugEfetivo) {
                try {
                  await trocarTemplateListaService(listaExistente.id, templateSlugEfetivo);
                  trackEvent('create_list_swapped_template', {
                    source: 'template_picker',
                    listId: listaExistente.id,
                    templateSlug: templateSlugEfetivo,
                  });
                } catch {
                  // se a troca falhar, ainda leva para a lista existente
                }
              }
              navigate(`/${listaExistente.slug}`, { replace: true });
            } else {
              navigate('/admin/listas', { replace: true });
            }
          } catch {
            navigate('/admin/listas', { replace: true });
          }
          return;
        }

        const normalizedMessage = rawMessage
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const message = normalizedMessage.includes('dados invalidos')
          ? 'Não foi possível criar a lista com esses dados.'
          : rawMessage || 'Não foi possível criar a lista agora.';
        trackEvent('create_list_error', {
          source: 'template_picker',
          templateSlug: templateSlugEfetivo,
          status: err?.response?.status,
          error: message,
        });
        setError(message);
      } finally {
        setCreating(false);
        submitLockRef.current = false;
      }
    },
    [isAuthenticated, loading, loginAsGuest, navigate, user?.email, user?.nome, user?.telefone]
  );

  // Checa se o usuário já tem lista; se tiver, encaminha pra ela (evita o erro "já tem lista")
  useEffect(() => {
    if (loading) return; // aguarda auth resolver
    let ativo = true;
    if (!user) {
      setVerificandoLista(false); // anônimo: vai criar como convidado, libera os botões
      return;
    }
    void listarMinhasListas()
      .then((listas) => {
        if (!ativo) return;
        const existente = listas.find((item) => item.slug);
        if (existente) {
          navigate(`/${existente.slug}`, { replace: true });
        } else {
          setVerificandoLista(false);
        }
      })
      .catch(() => {
        if (ativo) setVerificandoLista(false);
      });
    return () => {
      ativo = false;
    };
  }, [loading, user, navigate]);

  useEffect(() => {
    if (autoCreateStartedRef.current) return;
    if (!selectedTemplateSlug) return;
    if (loading) return; // aguarda auth resolver antes de tentar criar
    if (verificandoLista) return; // espera a checagem de lista existente

    autoCreateStartedRef.current = true;
    void executarCriacao(selectedTemplateSlug);
  }, [executarCriacao, selectedTemplateSlug, loading, verificandoLista]);

  if (verificandoLista) {
    return (
      <Box maxWidth={820} mx="auto" display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={820} mx="auto">
      <Stack spacing={2} mb={3} textAlign="center">
        <Typography variant="h4">Escolha o modelo da lista</Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione uma opção abaixo para criar sua lista automaticamente.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.5} justifyContent="center">
          {allowedTemplates.map((template) => {
            const criandoEste = creating && selectedTemplateSlug === template.slug;
            return (
              <Button
                key={template.slug}
                variant="outlined"
                size="large"
                disabled={creating || loading}
                onClick={() => void executarCriacao(template.slug)}
                startIcon={
                  criandoEste ? undefined : (
                    <Box
                      component="video"
                      src={
                        brandAssets.templateIconVideoSrc[
                          template.slug as keyof typeof brandAssets.templateIconVideoSrc
                        ]
                      }
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      sx={{
                        width: { xs: 40, sm: 52 },
                        height: { xs: 40, sm: 52 },
                        objectFit: 'cover',
                        borderRadius: '40%',
                        marginX: { xs: 0.5, sm: 1.5 },
                      }}
                    />
                  )
                }
                sx={{
                  py: 0,
                  px: { xs: 0.5, sm: 1 },
                  minHeight: { xs: 56, sm: 64 },
                  minWidth: { xs: '100%', sm: 200 },
                  flex: '1 1 200px',
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  color: brandTheme.palette.textPrimary,
                  borderColor: 'rgba(140, 117, 85, 0.3)',
                  borderWidth: 1.4,
                  background: '#ffffff',
                  boxShadow: '0 8px 16px rgba(90, 71, 47, 0.08)',
                  transition:
                    'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                  justifyContent: 'center',
                  '&:hover': {
                    borderColor: 'rgba(140, 117, 85, 0.44)',
                    background: '#ffffff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 22px rgba(90, 71, 47, 0.12)',
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: 0,
                    flex: { xs: '0 0 44px', sm: '0 0 58px' },
                    display: 'grid',
                    placeItems: 'center',
                  },
                }}
              >
                {criandoEste ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>Criando...</span>
                  </Stack>
                ) : (
                  <Box component="span" sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {template.label}
                  </Box>
                )}
              </Button>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CriarListaPage;
